import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star, PenLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { FadeIn, SectionHeader, EASE } from "./Reveal";
import { api, formatApiErrorDetail } from "../../lib/api";

const REVIEWS = [
    {
        name: "Marek Krzysztofek",
        job: "Remont łazienki",
        text: "Firma pana Jana zrobiła u nas kompletny remont łazienki od podstaw — płytki, hydraulika, biały montaż. Wszystko wykonane perfekcyjnie i dokładnie w ustalonym terminie. Ekipa Handy's to prawdziwi profesjonaliści.",
    },
    {
        name: "Anna Walendziak",
        job: "Wykończenie kuchni",
        text: "Handy's wykończyło nam kuchnię pod klucz: malowanie, szpachlowanie, montaż mebli i oświetlenia. Uczciwa wycena, żadnych niespodzianek po drodze. Z czystym sumieniem polecamy tę firmę dalej.",
    },
    {
        name: "Piotr Zamojski",
        job: "Instalacja elektryczna",
        text: "Zleciłem firmie Handy's wymianę całej instalacji elektrycznej w mieszkaniu. Solidnie, bezpiecznie i z pełnym porządkiem po pracach. Widać, że to ekipa z powołania, a nie przypadkowi ludzie.",
    },
    {
        name: "Katarzyna Olesińska",
        job: "Malowanie mieszkań",
        text: "Ściany pomalowane równo, bez ani jednej smugi — w końcu firma, która naprawdę dba o detale. Kontakt z Handy's błyskawiczny, termin dotrzymany co do dnia. Na pewno wrócimy z kolejnymi pracami.",
    },
    {
        name: "Tomasz Rduch",
        job: "Montaż i drobne naprawy",
        text: "Firma pana Jana zamontowała drzwi, listwy i karnisze oraz ogarnęła kilka drobnych napraw — wszystko w jeden dzień. Sprawnie, czysto i w rozsądnej cenie. Dokładnie tak ma wyglądać fachowa obsługa.",
    },
];

export default function Testimonials() {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [reviews, setReviews] = useState(REVIEWS);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ name: "", job: "", text: "", rating: 5 });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/reviews/approved");
                if (data.length) setReviews(data);
            } catch { /* fallback — przykładowe opinie */ }
        })();
    }, []);

    useEffect(() => {
        if (paused) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % reviews.length), 6000);
        return () => clearInterval(t);
    }, [paused, reviews.length]);

    const submitReview = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/reviews", form);
            toast.success("Dziękujemy za opinię!", {
                description: "Pojawi się na stronie po zatwierdzeniu przez firmę.",
            });
            setDialogOpen(false);
            setForm({ name: "", job: "", text: "", rating: 5 });
        } catch (err) {
            toast.error("Nie udało się wysłać opinii", {
                description: formatApiErrorDetail(err.response?.data?.detail),
            });
        } finally {
            setSending(false);
        }
    };

    const review = reviews[index] || REVIEWS[0];

    return (
        <section id="opinie" className="relative overflow-hidden bg-graphite py-24 md:py-32">
            <div className="bg-noise pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden />
            <div className="relative mx-auto max-w-5xl px-6 md:px-12">
                <SectionHeader dark index="04" eyebrow="Opinie klientów" title="Zaufanie buduje się pracą." className="mb-14" />
                <FadeIn delay={0.15}>
                    <div
                        className="relative rounded-[2rem] border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm md:p-14"
                        onMouseEnter={() => setPaused(true)}
                        onMouseLeave={() => setPaused(false)}
                        data-testid="testimonial-card"
                    >
                        <Quote className="absolute -top-6 left-8 h-12 w-12 rounded-2xl bg-gold p-2.5 text-ink md:left-14" />
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -24 }}
                                transition={{ duration: 0.55, ease: EASE }}
                            >
                                <div className="flex gap-1" aria-label={`Ocena ${review.rating || 5} na 5`}>
                                    {[...Array(review.rating || 5)].map((_, i) => (
                                        <Star key={i} className="h-5 w-5 fill-gold text-gold" />
                                    ))}
                                </div>
                                <p className="mt-7 font-display text-xl font-medium leading-relaxed text-white md:text-2xl">
                                    &bdquo;{review.text}&rdquo;
                                </p>
                                <div className="mt-8 flex items-center gap-4">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold font-display text-lg font-black text-ink">
                                        {review.name.charAt(0)}
                                    </span>
                                    <div>
                                        <p className="font-display font-bold text-white">{review.name}</p>
                                        <p className="text-sm text-white/50">{review.job}</p>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </FadeIn>
                <FadeIn delay={0.25}>
                    <div className="mt-8 flex items-center justify-between">
                        <div className="flex gap-2">
                            {reviews.map((_, i) => (
                                <button
                                    key={i}
                                    data-testid={`testimonial-dot-${i}`}
                                    onClick={() => setIndex(i)}
                                    aria-label={`Opinia ${i + 1}`}
                                    className={`h-2 rounded-full transition-[width,background-color] duration-400 ${
                                        i === index ? "w-8 bg-gold" : "w-2 bg-white/25 hover:bg-white/50"
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="flex gap-3">
                            <button
                                data-testid="testimonial-prev"
                                onClick={() => setIndex((index - 1 + reviews.length) % reviews.length)}
                                className="rounded-full border border-white/20 p-3 text-white transition-[background-color,border-color] duration-300 hover:border-gold hover:bg-gold hover:text-ink"
                                aria-label="Poprzednia opinia"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                data-testid="testimonial-next"
                                onClick={() => setIndex((index + 1) % reviews.length)}
                                className="rounded-full border border-white/20 p-3 text-white transition-[background-color,border-color] duration-300 hover:border-gold hover:bg-gold hover:text-ink"
                                aria-label="Następna opinia"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </FadeIn>
                <FadeIn delay={0.35}>
                    <div className="mt-10">
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <button
                                    data-testid="review-open-btn"
                                    className="inline-flex items-center gap-2 rounded-full border border-gold/60 px-8 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-gold transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-ink"
                                >
                                    <PenLine className="h-4 w-4" />
                                    Wystaw opinię
                                </button>
                            </DialogTrigger>
                            <DialogContent data-testid="review-dialog" className="rounded-3xl border-ink/10 bg-white p-8 sm:max-w-lg">
                                <DialogTitle className="font-display text-2xl font-bold tracking-tight text-ink">
                                    Oceń firmę Handy&rsquo;s
                                </DialogTitle>
                                <form onSubmit={submitReview} className="mt-6 space-y-5">
                                    <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Twoja ocena</label>
                                        <div className="flex gap-1.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <button
                                                    key={n}
                                                    type="button"
                                                    data-testid={`review-star-${n}`}
                                                    onClick={() => setForm({ ...form, rating: n })}
                                                    aria-label={`${n} gwiazdek`}
                                                >
                                                    <Star className={`h-7 w-7 transition-colors duration-200 ${n <= form.rating ? "fill-gold text-gold" : "text-ink/20"}`} />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="rv-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Imię i nazwisko</label>
                                            <Input id="rv-name" data-testid="review-name-input" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="np. Marek Krzysztofek" className="h-12 rounded-xl border-ink/10 focus-visible:ring-gold" />
                                        </div>
                                        <div>
                                            <label htmlFor="rv-job" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Zakres prac</label>
                                            <Input id="rv-job" data-testid="review-job-input" required minLength={2} value={form.job} onChange={(e) => setForm({ ...form, job: e.target.value })} placeholder="np. Remont łazienki" className="h-12 rounded-xl border-ink/10 focus-visible:ring-gold" />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="rv-text" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Twoja opinia</label>
                                        <Textarea id="rv-text" data-testid="review-text-input" required minLength={10} rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Jak oceniasz współpracę z firmą Handy's?" className="resize-none rounded-xl border-ink/10 focus-visible:ring-gold" />
                                    </div>
                                    <button
                                        data-testid="review-submit-btn"
                                        type="submit"
                                        disabled={sending}
                                        className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-60"
                                    >
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Wyślij opinię"}
                                    </button>
                                    <p className="text-center text-xs text-ink/40">Opinia zostanie opublikowana po zatwierdzeniu przez firmę.</p>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}
